import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../services/api';

interface FavoritesStore {
  favoriteIds: string[];
  isLoading: boolean;
  isSynced: boolean;

  loadFavorites: () => Promise<void>;
  addFavorite: (productId: string) => void;
  removeFavorite: (productId: string) => void;
  toggleFavorite: (productId: string) => void;
  isFavorite: (productId: string) => boolean;
  clearFavorites: () => void;
}

export const useFavoritesStore = create<FavoritesStore>()(
  persist(
    (set, get) => ({
      favoriteIds: [],
      isLoading: false,
      isSynced: false,

      loadFavorites: async () => {
        set({ isLoading: true });
        try {
          const products = await api.getFavorites();
          const serverIds = products.map((p) => p.id);
          
          set({ favoriteIds: serverIds, isSynced: true });
        } catch {
          // Keep local state on error
        } finally {
          set({ isLoading: false });
        }
      },

      addFavorite: (productId) => {
        if (get().favoriteIds.includes(productId)) return;
        set({ favoriteIds: [...get().favoriteIds, productId] });
        api.addFavorite(productId).catch(console.error);
      },

      removeFavorite: (productId) => {
        const prev = get().favoriteIds;
        set({ favoriteIds: prev.filter((id) => id !== productId) });
        api.removeFavorite(productId).catch(console.error);
      },

      toggleFavorite: (productId) => {
        if (get().isFavorite(productId)) {
          get().removeFavorite(productId);
        } else {
          get().addFavorite(productId);
        }
      },

      isFavorite: (productId) => {
        return get().favoriteIds.includes(productId);
      },

      clearFavorites: () => {
        set({ favoriteIds: [] });
      },
    }),
    {
      name: 'favorites-storage',
      partialize: (state) => ({ favoriteIds: state.favoriteIds }),
    }
  )
);
